// Elliott's draft of an Assessment, for the manager to review and edit: one
// section per capability (Team | Role | Capability, a brandy line between
// each), holding its one Roleplay with the capability's four levels beneath
// it and its one Quiz Question with the correct answer in green. The message
// the employee will see comes last.
//
// The same page shows an Assessment that is already running (`readOnly`): what
// it asks, as it was run, with nothing to change.

import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ElliottWorking from '../../../../../components/ui/ElliottWorking';
import { LEVEL_COLOR, LEVEL_NAME } from '../../Program/components/learning-lifecycle/CapabilityGapsList';
import { LEVEL_ORDER } from '../../Program/components/learning-lifecycle/teamCapability';
import {
  BODY,
  BRANDY,
  DONE,
  FONT,
  FormRow,
  INPUT_SX,
  LABEL,
  LINE,
  TEXT,
  TEXT_2,
  TEXT_3,
  TEXT_4,
  TEXTAREA_SX,
  WELL
} from '../designSystem';
import type { DraftItem } from './assessmentDraft';

/** What shows while Elliott writes the draft. */
export const DraftWriting: React.FC = () => (
  <Box sx={{ py: '10px' }}>
    <ElliottWorking message="Elliott is writing the draft..." size="inline" />
  </Box>
);

/** The small heading over the Roleplay and the Quiz Question within a section. */
const PartLabel: React.FC<{ children: React.ReactNode; note?: string }> = ({ children, note }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', mt: '4px' }}>
    <Typography sx={{ ...LABEL, color: BRANDY }}>{children}</Typography>
    {note && <Typography sx={{ ...BODY, fontSize: 11.5, color: TEXT_3 }}>{note}</Typography>}
  </Box>
);

/** One capability: its Roleplay with the four levels it is graded against, then its Quiz Question. */
const DraftSection: React.FC<{ item: DraftItem; onChange?: (item: DraftItem) => void; readOnly?: boolean }> = ({
  item,
  onChange,
  readOnly = false
}) => {
  const change = (next: DraftItem) => {
    if (!readOnly) onChange?.(next);
  };
  const setOption = (index: number, text: string) =>
    change({ ...item, quiz: { ...item.quiz, options: item.quiz.options.map((o, i) => (i === index ? text : o)) } });
  // Read-only: the same boxes, with no caret, no hover and no focus ring that says they can be typed in.
  const still = readOnly ? { cursor: 'default', '&:hover': {}, '&:focus': {} } : {};
  return (
    <FormRow label={item.capabilityName}>
      {(item.teamName || item.roleName) && (
        <Typography sx={{ ...BODY, fontSize: 12, color: TEXT_3, mt: '-6px' }}>
          {[item.teamName, item.roleName].filter(Boolean).join(' · ')}
        </Typography>
      )}
      <PartLabel note="Each learner answers once. Elliott matches the answer to one of the four levels below.">
        Roleplay
      </PartLabel>
      <Box
        component="textarea"
        rows={4}
        value={item.roleplay}
        aria-label={`Roleplay situation for ${item.capabilityName}`}
        readOnly={readOnly}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => change({ ...item, roleplay: e.target.value })}
        sx={{ ...TEXTAREA_SX, ...still }}
      />
      {/* The capability's levels, as set in Capability Setup: what the answer is graded against. */}
      <Box sx={{ border: `1px solid ${LINE}`, borderRadius: '4px', bgcolor: WELL }}>
        {LEVEL_ORDER.map((level, i) => (
          <Box
            key={level}
            sx={{ display: 'flex', alignItems: 'stretch', ...(i > 0 ? { borderTop: '1px solid #1F1F1F' } : {}) }}
          >
            <Box
              sx={{
                width: 104,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                px: '12px',
                borderRight: `1px solid ${LINE}`
              }}
            >
              <Typography sx={{ ...LABEL, color: LEVEL_COLOR[level] }}>{LEVEL_NAME[level]}</Typography>
            </Box>
            <Typography
              sx={{ ...BODY, flex: 1, fontSize: 12.5, lineHeight: 1.5, px: '12px', py: '8px', color: TEXT_2 }}
            >
              {item.levels[level] || 'Not set'}
            </Typography>
          </Box>
        ))}
      </Box>

      <PartLabel
        note={
          readOnly
            ? 'The correct answer is marked in green.'
            : 'Select the mark beside an answer to make it the correct one.'
        }
      >
        Quiz Question
      </PartLabel>
      <Box
        component="input"
        value={item.quiz.question}
        aria-label={`Quiz question for ${item.capabilityName}`}
        readOnly={readOnly}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          change({ ...item, quiz: { ...item.quiz, question: e.target.value } })
        }
        sx={{ ...INPUT_SX, ...still }}
      />
      <Box role="radiogroup" sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {item.quiz.options.map((option, i) => {
          const correct = i === item.quiz.correct;
          return (
            // The options are positional: edited in place, never reordered.
            // eslint-disable-next-line react/no-array-index-key
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Box
                component="button"
                type="button"
                role="radio"
                aria-checked={correct}
                aria-label={
                  correct ? 'Correct answer' : readOnly ? 'Not the correct answer' : 'Make this the correct answer'
                }
                disabled={readOnly}
                onClick={() => change({ ...item, quiz: { ...item.quiz, correct: i } })}
                sx={{
                  width: 22,
                  height: 22,
                  flexShrink: 0,
                  p: 0,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `1.5px solid ${correct ? DONE : TEXT_4}`,
                  bgcolor: correct ? DONE : 'transparent',
                  cursor: readOnly ? 'default' : 'pointer',
                  '&:hover': readOnly ? {} : { borderColor: DONE }
                }}
              >
                {correct && <CheckIcon sx={{ fontSize: 14, color: WELL }} />}
              </Box>
              <Box
                component="input"
                value={option}
                aria-label={`Answer ${i + 1}${correct ? ' (correct)' : ''}`}
                readOnly={readOnly}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOption(i, e.target.value)}
                sx={{
                  ...INPUT_SX,
                  ...still,
                  // The correct answer reads green.
                  ...(correct
                    ? {
                        borderColor: `${DONE}88`,
                        bgcolor: `${DONE}12`,
                        color: TEXT,
                        ...(readOnly ? {} : { '&:hover': { borderColor: DONE }, '&:focus': { borderColor: DONE } })
                      }
                    : {})
                }}
              />
            </Box>
          );
        })}
      </Box>
    </FormRow>
  );
};

/** Every capability's section, in order. Read-only for an Assessment that is already running. */
export const AssessmentDraftView: React.FC<{
  items: DraftItem[];
  onChange?: (items: DraftItem[]) => void;
  readOnly?: boolean;
}> = ({ items, onChange, readOnly = false }) => (
  <>
    {items.map((item) => (
      <DraftSection
        key={item.capabilityId}
        item={item}
        readOnly={readOnly}
        onChange={(next) => onChange?.(items.map((i) => (i.capabilityId === next.capabilityId ? next : i)))}
      />
    ))}
  </>
);

/** The message the employee sees on the Assessment before they start. Last on the page. */
export const MessageToEmployee: React.FC<{
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
}> = ({ value, onChange, readOnly = false }) => (
  <FormRow
    label="Message to learner"
    tip="What each employee reads when they open the Assessment from their dashboard, above the button that starts it."
  >
    <Box
      component="textarea"
      rows={4}
      value={value}
      aria-label="Message to learner"
      readOnly={readOnly}
      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange?.(e.target.value)}
      sx={{
        ...TEXTAREA_SX,
        fontFamily: FONT,
        ...(readOnly ? { cursor: 'default', '&:hover': {}, '&:focus': {} } : {})
      }}
    />
  </FormRow>
);
